import api from './api';
import type {
  ApiResponse,
  ClinicalVisit,
  VisitCategorizationReport,
  VisitPurposesMap,
} from '@/types';

// Visit Purpose v2: no visit_number — the ordinal is derived server-side.
// purpose is omitted entirely on travel-linked visits (the trip owns it).
export interface ClinicalVisitPayload {
  purpose?: string | null;
  occurred_on?: string | null;
  notes?: string | null;
  travel_id?: string | null;
}

export interface VisitReportFilters {
  date_from?: string;
  date_to?: string;
}

const buildParams = (filters: VisitReportFilters): string => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  return params.toString();
};

// The (visit number → typical purpose keys) SUGGESTION map (dropdown ordering
// only). Small + static per deploy, so callers can fetch once and cache.
export const getVisitPurposes = async (): Promise<VisitPurposesMap> => {
  const response = await api.get<ApiResponse<VisitPurposesMap>>('/clinical-visits/purposes');
  return response.data.data;
};

export const getLeadVisits = async (leadId: string): Promise<ClinicalVisit[]> => {
  const response = await api.get<ApiResponse<ClinicalVisit[]>>(`/leads/${leadId}/clinical-visits`);
  return response.data.data;
};

export const createLeadVisit = async (
  leadId: string,
  payload: ClinicalVisitPayload,
): Promise<ClinicalVisit> => {
  const response = await api.post<ApiResponse<ClinicalVisit>>(
    `/leads/${leadId}/clinical-visits`,
    payload,
  );
  return response.data.data;
};

export const updateVisit = async (
  visitId: string,
  payload: ClinicalVisitPayload,
): Promise<ClinicalVisit> => {
  const response = await api.put<ApiResponse<ClinicalVisit>>(
    `/clinical-visits/${visitId}`,
    payload,
  );
  return response.data.data;
};

export const deleteVisit = async (visitId: string): Promise<void> => {
  await api.delete(`/clinical-visits/${visitId}`);
};

export const getVisitCategorizationReport = async (
  filters: VisitReportFilters = {},
): Promise<VisitCategorizationReport> => {
  const query = buildParams(filters);
  const response = await api.get<ApiResponse<VisitCategorizationReport>>(
    `/reports/visit-categorization${query ? `?${query}` : ''}`,
  );
  return response.data.data;
};
